// src/App.js - Triton Design System Integration

import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { CssBaseline, Box, CircularProgress, Typography, Backdrop } from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';
import { SpeedInsights } from '@vercel/speed-insights/react';

// Triton Design System
import TritonTheme from './styles/TritonTheme';
import TritonLayout from './components/TritonLayout';

// Pages (Legacy - werden schrittweise überarbeitet)
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import VerifyEmailPage from './pages/VerifyEmailPage';
import PasswordResetPage from './pages/PasswordResetPage';
import TwoFAPage from './pages/TwoFAPage';
import DashboardPage from './pages/DashboardPage';
import LandingPage from './pages/LandingPage';
import PropertyLandingPage from './pages/PropertyLandingPage';
import ImmobilienAngebotePage from './pages/ImmobilienAngebotePage';
import SondereigentumsverwaltungPage from './pages/SondereigentumsverwaltungPage';
import VerkaeuferLoginPage from './pages/VerkaeuferLoginPage';
import FeaturesPage from './pages/FeaturesPage';
import PreisePage from './pages/PreisePage';
import KarrierePage from './pages/KarrierePage';
import ProfilBearbeiten from './pages/ProfilBearbeiten';
import Einstellungen from './pages/Einstellungen';
import EinladungenPage from './pages/mandanten/EinladungenPage';
import EinladungAnnehmenPage from './pages/mandanten/EinladungAnnehmenPage';
import AboFeaturesPage from './pages/mandanten/AboFeaturesPage';
import Impressum from './pages/Impressum';
import Kontakt from './pages/Kontakt';
import Datenschutz from './pages/Datenschutz';
import AGB from './pages/AGB';
import MaPortalPage from './pages/ma/MaPortalPage';
import MaScanPage from './pages/ma/MaScanPage';
import ScanBuchungPage from './pages/ScanBuchungPage';
import DienstplanPage from './pages/DienstplanPage';

// Legacy Komponenten (werden schrittweise in Triton-Design überführt)
import BookingTableEnhanced from './components/BookingTableEnhanced';
// ❌ ENTFERNT: MietvertragManagement (alte Version) - wird jetzt in TritonLayout verwendet
import Wohneinheiten from './components/WohneinheitenNew';
import ObjectManagement from './components/ObjectManagement';
import UserManagement from './components/UserManagement';

// Hooks
import { useAuth } from './hooks/useAuth';
import { AuthProvider } from './context/AuthContext';
import { rememberLoginRedirect } from './utils/loginRedirect';

// Loading Component im Triton-Design
const TritonLoader = ({ message = "Anwendung wird geladen..." }) => (
  <Backdrop 
    open={true} 
    sx={{ 
      zIndex: 9999,
      background: 'linear-gradient(135deg, #1a365d 0%, #2d3748 100%)',
      color: '#ffffff'
    }}
  >
    <Box sx={{ textAlign: 'center' }}>
      <CircularProgress 
        size={60} 
        sx={{ 
          color: '#d69e2e',
          mb: 3,
        }} 
      />
      <Typography variant="h6" sx={{ fontWeight: 500, mb: 1 }}>
        {message}
      </Typography>
      <Typography variant="body2" sx={{ opacity: 0.8 }}>
        Triton Design System - Professional Property Management
      </Typography>
    </Box>
  </Backdrop>
);

// Legacy-Wrapper für bestehende Komponenten
const LegacyWrapper = ({ children }) => (
  <Box sx={{ 
    p: 3, 
    backgroundColor: '#f7fafc', 
    minHeight: 'calc(100vh - 72px)',
    '& .MuiPaper-root': {
      boxShadow: '0px 1px 3px rgba(26, 54, 93, 0.08), 0px 1px 2px rgba(26, 54, 93, 0.06)',
      borderRadius: '12px',
      border: '1px solid #e2e8f0',
    }
  }}>
    {children}
  </Box>
);

// Geschützte Route: zeigt nur für authentifizierte Benutzer Inhalte.
// WICHTIG: Muss auf Modulebene definiert sein — eine Inline-Definition in
// AuthenticatedApp erzeugt bei jedem Render einen neuen Komponententyp und
// React remountet dann bei jeder Navigation den kompletten Seitenbaum
// (State-Verlust, z. B. offene Dialoge schließen sich).
const Protected = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    // Während Auth-Check: Kurzer Inline-Loader NUR innerhalb geschützter Bereiche
    return <TritonLoader message="Authentifizierung wird geprüft..." />;
  }
  if (!isAuthenticated) {
    const currentPath = `${location.pathname || ''}${location.search || ''}${location.hash || ''}`;
    rememberLoginRedirect(currentPath || '/dashboard');
    return <Navigate to="/login" replace />;
  }
  return children;
};

function AuthenticatedApp() {
  // Auth-Zustand ausschließlich aus AuthContext
  const { isAuthenticated, user, logout } = useAuth();

  const handleLogout = async () => {
    try {
      await logout();
      window.location.href = '/';
    } catch (err) {
      console.error('Logout error:', err);
      window.location.href = '/';
    }
  };

  if (isAuthenticated && user?.effective_role === 'zeiterfasser') {
    return (
      <Routes>
        <Route path="/karriere" element={<KarrierePage />} />
        <Route path="/scan" element={<ScanBuchungPage />} />
        <Route path="/dienstplan" element={<DienstplanPage />} />
        <Route
          path="/ma/scan"
          element={
            <Protected>
              <MaScanPage />
            </Protected>
          }
        />
        <Route
          path="/ma/*"
          element={
            <Protected>
              <MaPortalPage />
            </Protected>
          }
        />
        <Route path="*" element={<Navigate to="/ma" replace />} />
      </Routes>
    );
  }

  return (
    <Routes>
      {/* Authentifizierung */}
      <Route path="/" element={<LandingPage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/register" element={<RegisterPage />} />
      <Route path="/verify-email" element={<VerifyEmailPage />} />
      <Route path="/password-reset" element={<PasswordResetPage />} />
      <Route path="/2fa" element={<TwoFAPage />} />
      
      {/* Öffentliche Seiten */}
      <Route path="/scan" element={<ScanBuchungPage />} />
      <Route path="/dienstplan" element={<DienstplanPage />} />
      <Route path="/objekt/:slug" element={<PropertyLandingPage />} />
      <Route path="/immobilien" element={<ImmobilienAngebotePage />} />
      <Route path="/verkaeufer-login" element={<VerkaeuferLoginPage />} />
      <Route path="/leistungen/sondereigentumsverwaltung" element={<SondereigentumsverwaltungPage />} />
      <Route path="/karriere" element={<KarrierePage />} />
      <Route path="/features" element={<FeaturesPage />} />
      <Route path="/preise" element={<PreisePage />} />
      <Route path="/impressum" element={<Impressum />} />
      <Route path="/kontakt" element={<Kontakt />} />
      <Route path="/datenschutz" element={<Datenschutz />} />
      <Route path="/agb" element={<AGB />} />
      
      {/* Benutzer-Profil und Einstellungen */}
      <Route 
        path="/profil-bearbeiten" 
        element={
          <Protected>
            <TritonLayout user={user} onLogout={handleLogout}>
              <ProfilBearbeiten />
            </TritonLayout>
          </Protected>
        }
      />
      <Route 
        path="/einstellungen" 
        element={
          <Protected>
            <TritonLayout user={user} onLogout={handleLogout}>
              <Einstellungen />
            </TritonLayout>
          </Protected>
        }
      />
      <Route 
        path="/mandanten/einladungen" 
        element={
          <Protected>
            <TritonLayout user={user} onLogout={handleLogout}>
              <EinladungenPage />
            </TritonLayout>
          </Protected>
        }
      />
      <Route
        path="/ma/scan"
        element={
          <Protected>
            <MaScanPage />
          </Protected>
        }
      />
      <Route 
        path="/einstellungen/abo" 
        element={
          <Protected>
            <TritonLayout user={user} onLogout={handleLogout}>
              <AboFeaturesPage />
            </TritonLayout>
          </Protected>
        }
      />
      <Route 
        path="/einladung/:token" 
        element={
          <Protected>
            <TritonLayout user={user} onLogout={handleLogout}>
              <EinladungAnnehmenPage />
            </TritonLayout>
          </Protected>
        }
      />
      
      {/* Neues Triton Dashboard mit allen Routen */}
      <Route
        path="/dashboard/*"
        element={
          <Protected>
            <TritonLayout user={user} onLogout={handleLogout} />
          </Protected>
        }
      />
      
      {/* Legacy Routen (werden schrittweise auf Triton-Design umgestellt) */}
      <Route 
        path="/buchungen" 
        element={
          <Protected>
            <LegacyWrapper>
              <BookingTableEnhanced />
            </LegacyWrapper>
          </Protected>
        } 
      />
      {/* ❌ ENTFERNT: /mietvertrag Route - wird jetzt über TritonLayout unter /dashboard/mietvertrag bereitgestellt */}
      <Route 
        path="/wohneinheiten" 
        element={
          <Protected>
            <LegacyWrapper>
              <Wohneinheiten />
            </LegacyWrapper>
          </Protected>
        } 
      />
      <Route 
        path="/objekte" 
        element={
          <Protected>
            <LegacyWrapper>
              <ObjectManagement />
            </LegacyWrapper>
          </Protected>
        } 
      />
      <Route 
        path="/benutzer" 
        element={
          <Protected>
            <LegacyWrapper>
              <UserManagement />
            </LegacyWrapper>
          </Protected>
        } 
      />
      {/* Upload-Routen laufen ausschließlich über TritonLayout (/dashboard/*),
          damit die Dashboard-Shell (Sidebar/Header) erhalten bleibt. */}
      <Route path="/beleg-upload-v2" element={<Navigate to="/dashboard/belege" replace />} />
      <Route path="/beleg-upload-professional-v2" element={<Navigate to="/dashboard/belege" replace />} />
      <Route path="/csv-upload" element={<Navigate to="/dashboard/csv-upload" replace />} />
      <Route path="/sichere-dokumente" element={<Navigate to="/dashboard/belege" replace />} />
      <Route 
        path="/forderungsmanagement-v2" 
        element={
          <Protected>
            {(() => {
              const loc = window.location;
              const search = loc.search || '';
              const target = `/dashboard/forderungsmanagement${search}`;
              return <Navigate to={target} replace />;
            })()}
          </Protected>
        } 
      />
      
      {/* Legacy Dashboard als Fallback */}
      <Route 
        path="/dashboard-legacy" 
        element={
          <Protected>
            <LegacyWrapper>
              <DashboardPage />
            </LegacyWrapper>
          </Protected>
        } 
      />
    </Routes>
  );
}

function App() {
  return (
    <ThemeProvider theme={TritonTheme}>
      <CssBaseline />
      <Router
        future={{
          v7_startTransition: true,
          v7_relativeSplatPath: true
        }}
      >
        <AuthProvider>
          <AuthenticatedApp />
        </AuthProvider>
        <SpeedInsights />
      </Router>
    </ThemeProvider>
  );
}

export default App;
